import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import type { FilterStateItem } from "@racoon/ui";

type TabKey = "cars" | "shifts" | "repairs";

const AUTOPARK_STATES: FilterStateItem[] = [
  { key: "Машины", value: "cars" },
  { key: "Смены", value: "shifts" },
  { key: "Ремонты", value: "repairs" },
];

export const useAutoparkUrlState = () => {
  const [sp, setSp] = useSearchParams();

  /** ---------- BATCHED PATCHER (coalesces same-tick updates) ---------- */
  const pendingRef = useRef<URLSearchParams | null>(null);
  const flushIdRef = useRef<number | null>(null);

  const flushPatches = useCallback(() => {
    if (!pendingRef.current) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    flushIdRef.current = null;
    setSp(next, { replace: true });
  }, [setSp]);

  const patchMany = useCallback(
    (changes: Record<string, string | null>) => {
      const base = pendingRef.current ?? new URLSearchParams(sp);

      for (const [k, v] of Object.entries(changes)) {
        if (v == null || v === "") base.delete(k);
        else base.set(k, v);
      }

      pendingRef.current = base;

      if (flushIdRef.current == null) {
        flushIdRef.current = requestAnimationFrame(flushPatches);
      }
    },
    [sp, flushPatches],
  );

  // Cancel any pending flush on unmount so navigating away doesn't mutate the next page's URL.
  useEffect(() => {
    return () => {
      if (flushIdRef.current != null) {
        cancelAnimationFrame(flushIdRef.current);
        flushIdRef.current = null;
        pendingRef.current = null;
      }
    };
  }, []);

  const patch = useCallback((kv: Record<string, string | null>) => patchMany(kv), [patchMany]);

  // URL params (strings)
  const tabS = sp.get("tab") ?? "cars";

  const selectedState = AUTOPARK_STATES.find((s) => s.value === tabS) ?? AUTOPARK_STATES[0];

  const onStateChange = (v: FilterStateItem) => patch({ tab: v.value });

  return {
    tab: tabS as TabKey,
    selectedState,
    onStateChange,
    states: AUTOPARK_STATES,
  };
};
